/*
 * Promotional purchase limits
 * ---------------------------
 * Products with a positive custom.purchase_limit metafield are capped in the
 * product form, quick-buy, cart drawer and cart page. Checkout validation must
 * also enforce the same metafield server-side; storefront JavaScript alone can
 * always be bypassed by a direct request.
 */
(function () {
  'use strict';

  var LIMIT_SELECTOR = '[data-iits-purchase-limit]';
  var LINE_SELECTOR = '.mini-cart__line-item[data-iits-purchase-limit], .line-item[data-iits-purchase-limit]';
  var correctionInProgress = false;
  var observerQueued = false;

  function toPositiveInteger(value) {
    var number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  function getLimit(element) {
    return toPositiveInteger(element && element.getAttribute('data-limit'));
  }

  function getProductId(element) {
    return String(element && element.getAttribute('data-product-id') || '');
  }

  function getCartJsonUrl() {
    var cartUrl = window.routes && window.routes.cartUrl ? window.routes.cartUrl : '/cart';
    return cartUrl.replace(/\.js$/, '') + '.js';
  }

  function getCartUpdateUrl() {
    var cartUrl = window.routes && window.routes.cartUrl ? window.routes.cartUrl : '/cart';
    return cartUrl.replace(/\/$/, '') + '/update.js';
  }

  function setButtonAtLimit(button, atLimit) {
    if (!button) return;
    button.disabled = Boolean(atLimit);
    button.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
    button.classList.toggle('iits-quantity-button--at-limit', Boolean(atLimit));
  }

  function syncProductForm(form) {
    var marker = form.querySelector(LIMIT_SELECTOR);
    if (!marker) return;
    var limit = getLimit(marker);
    var input = form.querySelector('input[name="quantity"]');
    if (!limit || !input) return;
    input.max = String(limit);
    var minimum = toPositiveInteger(input.min) || 1;
    var value = toPositiveInteger(input.value) || minimum;
    input.value = String(Math.max(minimum, Math.min(value, limit)));
    var picker = input.closest('quantity-picker');
    if (picker) setButtonAtLimit(picker.querySelector('[data-action="increase-picker-quantity"]'), toPositiveInteger(input.value) >= limit);
  }

  function getLineTotals() {
    var totals = {};
    document.querySelectorAll(LINE_SELECTOR).forEach(function (line) {
      var productId = getProductId(line);
      var input = line.querySelector('.quantity-selector__value');
      if (!productId || !input) return;
      totals[productId] = (totals[productId] || 0) + (toPositiveInteger(input.value) || 0);
    });
    if (window.theme && window.theme.pageType === 'cart') {
      totals = {};
      document.querySelectorAll('tr' + LIMIT_SELECTOR).forEach(function (line) {
        var productId = getProductId(line);
        var input = line.querySelector('.hidden-tablet-and-up .quantity-selector__value') || line.querySelector('.quantity-selector__value');
        totals[productId] = (totals[productId] || 0) + (toPositiveInteger(input && input.value) || 0);
      });
    }
    return totals;
  }

  function syncCartControls() {
    var totals = getLineTotals();
    document.querySelectorAll(LINE_SELECTOR).forEach(function (line) {
      var limit = getLimit(line);
      var productId = getProductId(line);
      if (!limit || !productId) return;
      line.querySelectorAll('.quantity-selector').forEach(function (selector) {
        var input = selector.querySelector('.quantity-selector__value');
        if (!input) return;
        var ownQuantity = toPositiveInteger(input.value) || 0;
        var availableForLine = Math.max(0, limit - (totals[productId] || 0) + ownQuantity);
        input.max = String(availableForLine);
        var plus = selector.querySelector('[data-action="increase-quantity"]');
        if (plus) {
          plus.setAttribute('data-quantity', String(Math.min(ownQuantity + (toPositiveInteger(input.step) || 1), availableForLine)));
          setButtonAtLimit(plus, (totals[productId] || 0) >= limit || ownQuantity >= availableForLine);
        }
      });
    });
  }

  function syncAllControls() {
    document.querySelectorAll('form').forEach(syncProductForm);
    syncCartControls();
  }

  function setCheckoutBusy(isBusy) {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(function (button) {
      if (isBusy) {
        if (!button.disabled) button.setAttribute('data-iits-limit-disabled', 'true');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else if (button.getAttribute('data-iits-limit-disabled') === 'true') {
        button.disabled = false;
        button.removeAttribute('data-iits-limit-disabled');
        button.removeAttribute('aria-busy');
      }
    });
  }

  function displayFormError(form, message) {
    var container = form.querySelector('.product-form__payment-container') || form;
    var alert = form.querySelector('.iits-purchase-limit-alert');
    if (!alert) {
      alert = document.createElement('div');
      alert.className = 'iits-purchase-limit-alert';
      alert.setAttribute('role', 'alert');
      alert.setAttribute('aria-live', 'assertive');
      container.insertAdjacentElement('afterend', alert);
    }
    alert.textContent = message;
    alert.hidden = false;
  }

  function approveLimitedAdd(button, form, marker) {
    var limit = getLimit(marker);
    var productId = getProductId(marker);
    var quantityInput = form.querySelector('input[name="quantity"]');
    var requested = toPositiveInteger(quantityInput && quantityInput.value) || 1;
    var previousAlert = form.querySelector('.iits-purchase-limit-alert');
    var addApproved = false;
    if (previousAlert) previousAlert.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    fetch(getCartJsonUrl(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart could not be checked.');
        return response.json();
      })
      .then(function (cart) {
        var current = (cart.items || []).reduce(function (total, item) {
          return String(item.product_id) === productId ? total + item.quantity : total;
        }, 0);
        var remaining = Math.max(0, limit - current);
        if (remaining < 1) {
          displayFormError(form, 'Limit ' + limit + ' per customer. This product is already in your cart.');
          return;
        }
        if (quantityInput) quantityInput.value = String(Math.min(requested, remaining));
        addApproved = true;
        button.setAttribute('data-iits-limit-approved', 'true');
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.click();
      })
      .catch(function () {
        displayFormError(form, 'We could not verify this purchase limit. Please refresh the page and try again.');
      })
      .finally(function () {
        if (!addApproved) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
        syncAllControls();
      });
  }

  function correctExistingCart() {
    var limitElements = document.querySelectorAll(LINE_SELECTOR);
    if (!limitElements.length || correctionInProgress) return;
    var limits = {};
    limitElements.forEach(function (line) {
      var productId = getProductId(line);
      var limit = getLimit(line);
      if (productId && limit) limits[productId] = limit;
    });
    correctionInProgress = true;
    var cartVerified = false;
    setCheckoutBusy(true);
    fetch(getCartJsonUrl(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart could not be checked.');
        return response.json();
      })
      .then(function (cart) {
        var remaining = Object.assign({}, limits);
        var updates = {};
        var changed = false;
        (cart.items || []).forEach(function (item) {
          var productId = String(item.product_id);
          if (!limits[productId]) return;
          var allowed = Math.max(0, Math.min(item.quantity, remaining[productId] || 0));
          remaining[productId] = Math.max(0, (remaining[productId] || 0) - allowed);
          if (allowed !== item.quantity) {
            updates[item.key] = allowed;
            changed = true;
          }
        });
        if (!changed) {
          cartVerified = true;
          return false;
        }
        return fetch(getCartUpdateUrl(), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates: updates })
        }).then(function (response) {
          if (!response.ok) throw new Error('Cart could not be corrected.');
          cartVerified = true;
          return true;
        });
      })
      .then(function (changed) {
        if (!changed) return;
        if (window.theme && window.theme.pageType === 'cart') window.location.reload();
        else document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function () { setCheckoutBusy(true); })
      .finally(function () {
        correctionInProgress = false;
        setCheckoutBusy(!cartVerified);
        syncAllControls();
      });
  }

  document.addEventListener('click', function (event) {
    var productPlus = event.target.closest('[data-action="increase-picker-quantity"]');
    if (productPlus) {
      var productForm = productPlus.closest('form');
      var productMarker = productForm && productForm.querySelector(LIMIT_SELECTOR);
      var productInput = productForm && productForm.querySelector('input[name="quantity"]');
      if (productMarker && productInput && toPositiveInteger(productInput.value) >= getLimit(productMarker)) {
        event.preventDefault(); event.stopImmediatePropagation(); setButtonAtLimit(productPlus, true); return;
      }
    }
    var cartPlus = event.target.closest('.mini-cart__line-item[data-iits-purchase-limit] [data-action="increase-quantity"], .line-item[data-iits-purchase-limit] [data-action="increase-quantity"]');
    if (cartPlus && cartPlus.disabled) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    var addButton = event.target.closest('buy-button button[type="submit"]');
    if (!addButton) return;
    var form = addButton.closest('form');
    var marker = form && form.querySelector(LIMIT_SELECTOR);
    if (!marker) return;
    if (addButton.getAttribute('data-iits-limit-approved') === 'true') {
      addButton.removeAttribute('data-iits-limit-approved'); return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    approveLimitedAdd(addButton, form, marker);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var marker = form && form.querySelector && form.querySelector(LIMIT_SELECTOR);
    if (!marker) return;
    var button = form.querySelector('buy-button button[type="submit"]');
    if (!button || button.getAttribute('data-iits-limit-approved') === 'true') return;
    event.preventDefault(); event.stopImmediatePropagation(); button.click();
  }, true);

  document.addEventListener('input', function (event) {
    var input = event.target.closest && event.target.closest('.quantity-selector__value');
    if (!input) return;
    var form = input.closest('form');
    var marker = form && form.querySelector(LIMIT_SELECTOR);
    if (marker && input.name === 'quantity') {
      var productLimit = getLimit(marker);
      if (toPositiveInteger(input.value) > productLimit) input.value = String(productLimit);
      syncProductForm(form); return;
    }
    var line = input.closest(LINE_SELECTOR);
    if (line) {
      var totals = getLineTotals();
      var productId = getProductId(line);
      var limit = getLimit(line);
      var current = toPositiveInteger(input.value) || 0;
      var allowed = Math.max(0, limit - (totals[productId] || 0) + current);
      if (current > allowed) input.value = String(allowed);
      syncCartControls();
    }
  }, true);

  document.addEventListener('change', syncAllControls, true);
  document.addEventListener('product:added', function () {
    window.setTimeout(function () { syncAllControls(); correctExistingCart(); }, 0);
  });

  function queueSync() {
    if (observerQueued) return;
    observerQueued = true;
    window.requestAnimationFrame(function () { observerQueued = false; syncAllControls(); });
  }

  function start() {
    syncAllControls();
    correctExistingCart();
    var observer = new MutationObserver(queueSync);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */
